import React, { FC, useEffect, useState } from 'react';
import {
  ForgeButton,
  ForgeIcon,
  ForgeIconButton,
  ForgeInlineMessage,
  ForgeSelect
} from '@tylertech/forge-react';

import './index.scss';
import isEmpty from 'lodash/isEmpty';
import { ExportFormat, scheduledSendGeospatialFormats } from 'common/downloadLinks';
import { AttachmentConfiguration, AttachmentType, TableAttachment } from '../../types/attachment';
import { ATTACHMENT_ICONS, DEFAULT_ATTACHMENT_CONFIG } from '../../constants';
import { I18n } from 'common/visualizations';
import { generateDefaultPdfConfig, getExportFormatOptions, getTableOptions, joinTableNames } from './helpers';
import { ScheduleSendValues } from '../../types/scheduleSend';
import PdfConfiguration from './PdfConfiguration';

const scope = 'shared.components.schedule_send_report.modal.attachment_section';

export interface AttachmentSectionProps {
  tableList: TableAttachment[] | void;
  attachmentConfigs: AttachmentConfiguration[];
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
}

const AttachmentSection: FC<AttachmentSectionProps> = (props) => {
  const { tableList, attachmentConfigs, updateScheduleSendValue } = props;
  const [multiHierarchyTable, setMultiHierarchyTable] = useState<string[]>([]);

  const handleAddAttachment = () => {
    updateScheduleSendValue('attachmentConfigs', [
      ...attachmentConfigs,
      {
        value: DEFAULT_ATTACHMENT_CONFIG.value,
        format: DEFAULT_ATTACHMENT_CONFIG.format,
        type: DEFAULT_ATTACHMENT_CONFIG.type as AttachmentType,
        name: DEFAULT_ATTACHMENT_CONFIG.name
      }
    ]);
  };

  useEffect(() => {
    const tableMap = new Map(tableList?.map((table) => [table.componentUuid, table]) ?? []);
    const namesSet = new Set<string>();
    for (const config of attachmentConfigs) {
      const { componentUuid, value } = config;
      const tableInfo = (componentUuid && tableMap.get(componentUuid)) || tableMap.get(value);

      if (tableInfo?.withMultipleHierarchy) {
        namesSet.add(tableInfo.title);
      }
    }

    const multipleHierarchyNames = Array.from(namesSet);
    setMultiHierarchyTable(multipleHierarchyNames);
  }, [attachmentConfigs, tableList]);

  const handleRemoveAttachment = (index: number) => {
    const newAttachmentConfigs = attachmentConfigs.filter((_, i) => i !== index);
    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const onTableChange = (index: number, event: CustomEvent) => {
    const value = event.detail as string;
    const newAttachmentConfigs = [...attachmentConfigs];
    newAttachmentConfigs[index].value = value;

    const selectedTable = tableList?.find((table) => table.componentUuid === value);
    const isTableWithGeoData = selectedTable?.withGeoSpatialData;

    // if selected table does not contain geospatial column and the selected export type is for GeoSpatial formats only.
    const isGeoDataNotApplicable =
      !isTableWithGeoData &&
      scheduledSendGeospatialFormats.includes(attachmentConfigs[index].format as ExportFormat);

    // we only support pdf format type for full report attachment
    if (value === 'full-report' || isGeoDataNotApplicable) {
      newAttachmentConfigs[index].format = 'pdf';
    }

    if (value === 'full-report') {
      newAttachmentConfigs[index].type = 'full_report';
    } else {
      newAttachmentConfigs[index].type = 'table_export';
      newAttachmentConfigs[index].name = selectedTable?.title || '';
      newAttachmentConfigs[index].componentUuid = selectedTable?.componentUuid;
    }

    // Generate PDF config for table exports with PDF format
    if (newAttachmentConfigs[index].type === 'table_export' && newAttachmentConfigs[index].format === 'pdf') {
      const isTableWithGrouping = Boolean(selectedTable?.withGrouping);
      newAttachmentConfigs[index].pdfOptions = generateDefaultPdfConfig(isTableWithGrouping);
    }

    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const onFormatChange = (index: number, event: CustomEvent) => {
    const value = event.detail as string;
    const newAttachmentConfigs = [...attachmentConfigs];
    newAttachmentConfigs[index].format = value;

    if (value !== 'pdf' && newAttachmentConfigs[index].pdfOptions) {
      delete newAttachmentConfigs[index].pdfOptions;
    } else if (value === 'pdf' && !newAttachmentConfigs[index].pdfOptions) {
      const isTableWithGrouping = Boolean(
        tableList?.find((table) => table.componentUuid === attachmentConfigs[index].value)?.withGrouping
      );
      newAttachmentConfigs[index].pdfOptions = generateDefaultPdfConfig(isTableWithGrouping);
    }

    updateScheduleSendValue('attachmentConfigs', newAttachmentConfigs);
  };

  const attachmentOptions = (index: number, totalAttachments: number) => {
    const tableCurrentValue = isEmpty(attachmentConfigs[index].value)
      ? DEFAULT_ATTACHMENT_CONFIG.value
      : attachmentConfigs[index].value;

    const formatCurrentValue = isEmpty(attachmentConfigs[index].format)
      ? DEFAULT_ATTACHMENT_CONFIG.format
      : attachmentConfigs[index].format;

    const isTableWithGrouping = Boolean(
      tableList?.find((table) => table.componentUuid === tableCurrentValue)?.withGrouping
    );

    return (
      <div className="attachment-select-dropdrowns">
        <ForgeSelect
          className="attachment-table-list"
          data-testid="attachment-table-select"
          value={tableCurrentValue}
          options={getTableOptions(tableList)}
          syncPopupWidth
          on-change={(event: CustomEvent) => onTableChange(index, event)}
        >
          <ForgeIcon slot="start" name={ATTACHMENT_ICONS[attachmentConfigs[index].type]} />
        </ForgeSelect>

        <ForgeSelect
          className="attachment-format-list"
          data-testid="attachment-format-select"
          value={formatCurrentValue}
          options={getExportFormatOptions(tableList, tableCurrentValue)}
          disabled={tableCurrentValue === 'full-report'}
          syncPopupWidth
          on-change={(event: CustomEvent) => onFormatChange(index, event)}
        />

        <PdfConfiguration
          index={index}
          tableCurrentValue={tableCurrentValue}
          formatCurrentValue={formatCurrentValue}
          attachmentConfigs={attachmentConfigs}
          updateScheduleSendValue={updateScheduleSendValue}
          isTableWithGrouping={isTableWithGrouping}
        />

        <ForgeIconButton
          densityLevel={3}
          slot="trailing"
          data-testid="remove-attachment-btn"
          className="remove-attachment"
          hidden={totalAttachments <= 1}
          dense
          onClick={() => {
            handleRemoveAttachment(index);
          }}
        >
          <ForgeIcon slot="end" name="close" />
        </ForgeIconButton>
      </div>
    );
  };

  return (
    <div className="attachment-section" data-testid="attachment-section">
      <div className="contents">
        <h1 className="forge-typography--heading3" id="attachment-title" data-testid="attachment-title">
          {I18n.t('title', { scope })}
        </h1>

        {!isEmpty(multiHierarchyTable) && (
          <ForgeInlineMessage
            theme="info"
            data-testid="multi-tab-attachment-banner"
            className="multi-tab-content"
          >
            <ForgeIcon slot="icon" name="info"></ForgeIcon>
            <p>
              <span>
                <b>{joinTableNames(multiHierarchyTable)}</b>
              </span>{' '}
              {/* Safe: translation content only, no user input */}
              <span
                dangerouslySetInnerHTML={{
                  __html: I18n.t('multi_tabs_banner', {
                    scope,
                    containLabel:
                      multiHierarchyTable.length > 1
                        ? I18n.t('contain', { scope })
                        : I18n.t('contains', { scope }),
                    thisTableLabel:
                      multiHierarchyTable.length > 1
                        ? I18n.t('these_tables', { scope })
                        : I18n.t('this_table', { scope })
                  })
                }}
              ></span>
            </p>
          </ForgeInlineMessage>
        )}

        <div className="select-section">
          {attachmentConfigs.map((config, index) => (
            <div key={`${config.value}-${index}`}>{attachmentOptions(index, attachmentConfigs.length)}</div>
          ))}
        </div>

        <ForgeButton
          data-testid="add-attachment-btn"
          className="add-attachment"
          onClick={handleAddAttachment}
        >
          <ForgeIcon name="add" />
          <span>{I18n.t('add', { scope })}</span>
        </ForgeButton>
      </div>
    </div>
  );
};

export default AttachmentSection;
